'use client';

/**
 * FormSuccessPage — Themed thank-you / success screen shown after submission.
 *
 * Supports:
 *  - Custom success message (from form_configurations.success_message)
 *  - Custom success image (success_image_url)
 *  - Optional submitted-data summary table
 *  - "Submit another response" button
 *  - Auto-redirect countdown
 *
 * Phase 5, task 5.4 of the Total Forms Renovation Plan.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { CheckCircle2, ExternalLink, RotateCcw, PartyPopper } from 'lucide-react';
import { type FormThemeConfig, resolveFormTheme, isThemeDark } from './formThemes';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface SuccessPageConfig {
  message?: string;
  image_url?: string;
  redirect_url?: string;
  redirect_delay?: number; // seconds, 0 = no redirect
  show_summary?: boolean;
  allow_another?: boolean;
}

export interface FormSuccessPageProps {
  config: SuccessPageConfig;
  formTheme?: Partial<FormThemeConfig> | null;
  /** Flat key→value map of submitted data for optional summary table */
  submittedData?: Record<string, unknown>;
  /** Label map: field_key → human label */
  fieldLabels?: Record<string, string>;
  onSubmitAnother?: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatValue(val: unknown): string {
  if (val === null || val === undefined || val === '') return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (Array.isArray(val)) return val.map(formatValue).join(', ');
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export default function FormSuccessPage({
  config,
  formTheme,
  submittedData,
  fieldLabels,
  onSubmitAnother,
}: FormSuccessPageProps) {
  const ft = resolveFormTheme(formTheme);
  const dark = isThemeDark(ft);

  const message = config.message || 'Thank you for your submission!';
  const redirectUrl = config.redirect_url || null;
  const delay = config.redirect_delay ?? 0;
  const showSummary = config.show_summary && submittedData && Object.keys(submittedData).length > 0;

  // ── Auto-redirect countdown ──────────────────────────────────────────────
  const [countdown, setCountdown] = useState(delay);

  useEffect(() => {
    if (!redirectUrl || delay <= 0) return;
    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timer);
          window.location.href = redirectUrl;
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [redirectUrl, delay]);

  // ── Summary rows ─────────────────────────────────────────────────────────
  const summaryRows = useMemo(() => {
    if (!showSummary || !submittedData) return [];
    return Object.entries(submittedData)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([key, value]) => ({
        label: fieldLabels?.[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        value: formatValue(value),
      }));
  }, [showSummary, submittedData, fieldLabels]);

  return (
    <div
      className="min-h-[60vh] flex flex-col items-center justify-center px-6 py-16 text-center"
      style={{ background: ft.bg_color, fontFamily: ft.font_family }}
    >
      {/* Success image or check icon */}
      {config.image_url ? (
        <div className="mb-6 w-full max-w-xs">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={config.image_url}
            alt="Success"
            className="w-full rounded-2xl shadow-md object-cover max-h-48"
            style={{ borderRadius: ft.border_radius ? `${ft.border_radius}px` : undefined }}
          />
        </div>
      ) : (
        <div className="mb-6 relative">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center shadow-lg"
            style={{ background: `${ft.accent_color}18` }}
          >
            <CheckCircle2 className="w-10 h-10" style={{ color: ft.accent_color }} />
          </div>
          <PartyPopper
            className="absolute -top-2 -right-2 w-6 h-6 animate-bounce"
            style={{ color: ft.accent_color }}
          />
        </div>
      )}

      {/* Message */}
      <h2
        className="text-2xl md:text-3xl font-bold leading-tight max-w-lg"
        style={{ color: ft.question_color }}
      >
        {message}
      </h2>

      {/* Redirect countdown */}
      {redirectUrl && delay > 0 && countdown > 0 && (
        <p className="mt-3 text-sm opacity-60" style={{ color: ft.answer_color }}>
          Redirecting in {countdown}s…
        </p>
      )}

      {/* Summary table */}
      {showSummary && summaryRows.length > 0 && (
        <div
          className="mt-8 w-full max-w-md rounded-xl overflow-hidden shadow-sm border text-left"
          style={{
            background: ft.card_color,
            borderColor: ft.input_border_color || (dark ? '#ffffff15' : '#00000010'),
          }}
        >
          <div
            className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider"
            style={{ background: `${ft.accent_color}12`, color: ft.accent_color }}
          >
            Submission Summary
          </div>
          <div className="divide-y" style={{ borderColor: ft.input_border_color || (dark ? '#ffffff10' : '#00000008') }}>
            {summaryRows.map((row, i) => (
              <div key={i} className="px-4 py-2.5 flex justify-between gap-4 text-sm">
                <span className="font-medium opacity-70" style={{ color: ft.question_color }}>
                  {row.label}
                </span>
                <span className="text-right" style={{ color: ft.answer_color }}>
                  {row.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="mt-10 flex flex-wrap gap-3 justify-center">
        {config.allow_another && onSubmitAnother && (
          <button
            onClick={onSubmitAnother}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold shadow transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{
              background: ft.header_gradient || ft.accent_color,
              color: '#fff',
              borderRadius: ft.border_radius ? `${ft.border_radius}px` : undefined,
            }}
          >
            <RotateCcw className="w-4 h-4" />
            Submit Another Response
          </button>
        )}

        {redirectUrl && (
          <a
            href={redirectUrl}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-medium border transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{
              borderColor: ft.accent_color,
              color: ft.accent_color,
              borderRadius: ft.border_radius ? `${ft.border_radius}px` : undefined,
            }}
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Continue
          </a>
        )}
      </div>
    </div>
  );
}
